import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { SupportService } from 'apps/app-cic-ciam/src/services/support.service';
import { produce } from 'immer';
import {
  VerifyIdFailure,
  VerifyIdSuccess,
  VerifySupplierId,
  VerifyWorkerId,
} from '..';
import { TranslateService } from '@ngx-translate/core';

export interface UsernameStateModel {
  workerId?: string | null;
  supplierId?: string | null;

  isLoading: boolean;
  error?: any;

  userInfo?: { email?: string | null; phone?: string | null } | null;
}

const USERNAME_STATE_TOKEN = new StateToken<UsernameStateModel>('username');

@State<UsernameStateModel>({
  name: USERNAME_STATE_TOKEN,
  defaults: {
    workerId: undefined,
    supplierId: undefined,

    isLoading: false,
    error: null,

    userInfo: null,
  },
})
@Injectable()
export class UsernameState {
  constructor(
    private supportService: SupportService,
    private readonly translate: TranslateService
  ) {}

  @Selector()
  static getUserInfo(state: UsernameStateModel) {
    return state.userInfo;
  }

  @Selector()
  static isLoading(state: UsernameStateModel) {
    return state.isLoading;
  }

  @Action(VerifyWorkerId)
  async verifyWorkerId(
    ctx: StateContext<UsernameStateModel>,
    action: VerifyWorkerId
  ) {
    try {
      ctx.patchState({ isLoading: true });
      const response = await this.supportService.forgotUsername(
        action.workerId,
        null,
        action.recaptchaV2Token,
        action.recaptchaV3Token
      );
      ctx.patchState({ isLoading: false });

      if (response.success) {
        const result = response.value;

        if (result) {
          ctx.dispatch(new VerifyIdSuccess(result));
        } else {
          ctx.dispatch(new VerifyIdFailure(response.errorMessage));
        }
      } else {
        ctx.dispatch(new VerifyIdFailure(response.errorMessage));
      }
    } catch (error: any) {
      let _error = null;

      if (error instanceof HttpErrorResponse) {
        if (error.error && 'errorMessage' in error.error) {
          _error = error.error.errorMessage;
        } else {
          _error = error.message;
        }
      } else {
        _error = error;
      }

      if (typeof _error == "string") {
        _error = this.translate.instant(_error)
      }

      ctx.dispatch(new VerifyIdFailure(_error));
    }
  }

  @Action(VerifySupplierId)
  async verifySupplierId(
    ctx: StateContext<UsernameStateModel>,
    action: VerifySupplierId
  ) {
    try {
      ctx.patchState({ isLoading: true });
      const response = await this.supportService.forgotUsername(
        null,
        action.supplierId,
        action.recaptchaV2Token,
        action.recaptchaV3Token
      );
      ctx.patchState({ isLoading: false });

      if (response.success) {
        const result = response.value;

        if (result) {
          ctx.dispatch(new VerifyIdSuccess(result));
        } else {
          ctx.dispatch(new VerifyIdFailure());
        }
      } else {
        ctx.dispatch(new VerifyIdFailure());
      }
    } catch (error: any) {
      let _error = null;

      if (error instanceof HttpErrorResponse) {
        if (error.error && 'errorMessage' in error.error) {
          _error = error.error.errorMessage;
        } else {
          _error = error.message;
        }
      } else {
        _error = error;
      }

      if (typeof _error == "string") {
        _error = this.translate.instant(_error)
      }

      ctx.dispatch(new VerifyIdFailure(_error));
    }
  }

  @Action(VerifyIdFailure)
  verifyFailure(ctx: StateContext<UsernameStateModel>, event: VerifyIdFailure) {
    ctx.setState(
      produce((draft) => {
        draft.isLoading = false;
        draft.error = event.error;
      })
    );
  }

  @Action(VerifyIdSuccess)
  verifySuccess(ctx: StateContext<UsernameStateModel>, event: VerifyIdSuccess) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        if (event.userInfo) {
          draft.isLoading = false;
          draft.workerId = event.userInfo.workerId;
          draft.userInfo = {
            ...event.userInfo,
            phone: event.userInfo.mobilePhone,
          };
        }
      })
    );
  }
}
